import React, { useMemo, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Palette, Boxes, UserCog, Gauge, Square, Sparkles } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { AdminTabs } from '../shared/AdminTabs';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Card } from '../../../components/ui/Card';
import { PRIMARY, SUCCESS, WARNING, DANGER, INFO, XP, NEUTRAL, ROLE_COLORS, MASTERY, MASTERY_THRESHOLDS, RADIUS, SHADOW, BADGE_VARIANTS, MODULE_COLOR_MAP } from '../../../config/designTokens';
// ─── Tab config ──────────────────────────────────────────────────────────────
type TabId = 'colors' | 'modules' | 'roles' | 'mastery' | 'shape';
const TABS: Array<{
  id: TabId;
  label: string;
  icon: React.ReactNode;
}> = [{
  id: 'colors',
  label: 'Kleuren',
  icon: <Palette className="w-4 h-4" data-id="element-6987" data-mp="1iy12xt8kr1mc" />
}, {
  id: 'modules',
  label: 'Modules',
  icon: <Boxes className="w-4 h-4" data-id="element-6988" data-mp="1nwkmjmzp41bj" />
}, {
  id: 'roles',
  label: 'Rollen',
  icon: <UserCog className="w-4 h-4" data-id="element-6989" data-mp="2c44i9xf4l4ek" />
}, {
  id: 'mastery',
  label: 'Mastery',
  icon: <Gauge className="w-4 h-4" data-id="element-6990" data-mp="28214v38wbpqn" />
}, {
  id: 'shape',
  label: 'Vorm',
  icon: <Square className="w-4 h-4" data-id="element-6991" data-mp="284432ak7cp6g" />
}];
/**
 * Provenance per tab — welk bestand, welke export, en wie eruit leest.
 *
 * Deze gegevens stonden tot 12 aug 2026 in het generieke SSOT-register, dat
 * daarnaast dezelfde tokens in tabelvorm toonde. Die dubbele surface is opgeheven;
 * de herkomst is meeverhuisd, want dát was het enige wat het register wél had en
 * deze pagina niet. Een swatch zonder bronvermelding laat je zien hoe iets eruit
 * ziet, niet waar je het verandert.
 */
const TAB_PROVENANCE: Record<TabId, {
  file: string;
  exportName: string;
  consumers: string;
}> = {
  colors: {
    file: 'src/config/designTokens.ts',
    exportName: 'PRIMARY / SUCCESS / WARNING / DANGER / INFO / XP / NEUTRAL',
    consumers: 'Badge, Button, feedback-surfaces, GlanceBar'
  },
  modules: {
    file: 'src/content/moduleColors.ts',
    exportName: 'MODULE_COLOR_MAP (via config/designTokens)',
    consumers: 'getModuleColors / getModuleHexColor / getModuleTint, admin moduleMetadata, HomePage / SubjectsPage / ProgressPage'
  },
  roles: {
    file: 'src/config/designTokens.ts',
    exportName: 'ROLE_COLORS',
    consumers: 'uiHelpers (re-export), ProfileButton, portal-shells'
  },
  mastery: {
    file: 'src/config/designTokens.ts',
    exportName: 'MASTERY + MASTERY_THRESHOLDS',
    consumers: 'getMasteryBand, MasteryBar, MasteryRing'
  },
  shape: {
    file: 'src/config/designTokens.ts',
    exportName: 'RADIUS / SHADOW / BADGE_VARIANTS',
    consumers: 'CARD, INPUT_STUDENT, Button, Card, Badge'
  }
};
const TAB_DESCRIPTION: Record<TabId, string> = {
  colors: 'Brand- en semantische status-tokens. Map op betekenis, niet op hue — danger is rood, warning amber, los van elkaar.',
  modules: 'Het canonieke kleur-palet per leer-module, inclusief de subtiele per-wereld practice-tint (APP_FEEL §3.1).',
  roles: 'Kleuridentiteit per account-rol — teacher violet, parent emerald, learner oranje, admin amber.',
  mastery: 'De vier beheersings-banden met eigen drempels (75/55/35). Hergebruikt nooit status-kleuren: beheersing ≠ correctheid.',
  shape: 'De getokeniseerde radius- en shadow-schaal plus de badge-varianten die de hele app vormgeven.'
};
// ─── Page ──────────────────────────────────────────────────────────────────
/**
 * DesignSystemPage — dedicated, read-only, LIVE showcase of the design-system
 * SSOT (src/config/designTokens.ts). Every swatch reads its value live from the
 * token export at render time, so the surface can never drift from the running
 * app. Tailwind class strings are rendered as-is (they all live in the token
 * source, so they survive purge); raw hex values use inline styles.
 */
export function DesignSystemPage() {
  const [activeTab, setActiveTab] = useState<TabId>('colors');
  const moduleCount = useMemo(() => Object.keys(MODULE_COLOR_MAP).length, []);
  return <PageLayout title="Design System" subtitle="De design-tokens SSOT — live uit de code gelezen, nooit gekopieerd" actions={<ConnectionBadge status="live" data-id="element-6993" data-mp="1od8jhspnvmpw" />} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Design System'
  }]} data-id="element-6992" data-mp="1jex6xa7zey3z">
      
      {/* Summary header */}
      <Card className="p-5 mb-5" data-id="element-6994" data-mp="1sdimqxm2jz12">
        <div className="flex items-start gap-4" data-id="element-6995" data-mp="1xf81oocewwuf">
          <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0" data-id="element-6996" data-mp="1xdwyoqqsjt4d">
            <Palette className="w-5 h-5" data-id="element-6997" data-mp="10mma9y3x7web" />
          </div>
          <div className="min-w-0" data-id="element-6998" data-mp="14qu2w6di1wht">
            <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-6999" data-mp="14ny0o5umclnk">
              Design-tokens
            </h2>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed" data-id="element-7000" data-mp="1efkbi131oips">
              Eén canonieke bron voor kleur, vorm en module-palet:{' '}
              <code className="font-mono text-[12px] text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-7001" data-mp="gzoolvn7jc9m">
                src/config/designTokens.ts
              </code>
              . Bevat o.a.{' '}
              <span className="font-semibold text-gray-900 tabular-nums" data-id="element-7002" data-mp="eepbkgc5wk7r">
                {moduleCount}
              </span>{' '}
              module-kleursets. Componenten importeren hier vandaan in plaats
              van kleurklassen te hardcoden.
            </p>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <AdminTabs tabs={TABS} activeTab={activeTab} onTabChange={id => setActiveTab(id as TabId)} data-id="element-7003" data-mp="ibq6w4ibyo0" />
      

      <p className="text-sm text-gray-500 mb-3" data-id="element-7004" data-mp="3rod7vniz9qim">{TAB_DESCRIPTION[activeTab]}</p>

      {/* Herkomst: waar deze tokens staan en wie ze leest. */}
      <dl className="mb-5 grid gap-x-6 gap-y-1 rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs sm:grid-cols-[auto,1fr]" data-id="element-7005" data-mp="3hvyzwd1b3no0">
        <dt className="font-semibold text-gray-500" data-id="element-7006" data-mp="3czh1rw0henkn">Bron</dt>
        <dd className="font-mono text-gray-700 break-all" data-id="element-7007" data-mp="38wbz9tgeilw0">
          {TAB_PROVENANCE[activeTab].file}
        </dd>
        <dt className="font-semibold text-gray-500" data-id="element-7008" data-mp="33y0tbtt88vj2">Export</dt>
        <dd className="font-mono text-gray-700 break-all" data-id="element-7009" data-mp="2u9lpe3om3kyt">
          {TAB_PROVENANCE[activeTab].exportName}
        </dd>
        <dt className="font-semibold text-gray-500" data-id="element-7010" data-mp="2yb77q4jx0rge">Gelezen door</dt>
        <dd className="text-gray-600" data-id="element-7011" data-mp="2kb5xvzloa9os">
          {TAB_PROVENANCE[activeTab].consumers}
        </dd>
      </dl>

      <motion.div key={activeTab} initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.22
    }} role="tabpanel" id={`tabpanel-${activeTab}`} data-id="element-7012" data-mp="2pa0mxja8guhc">
        
        {activeTab === 'colors' && <ColorsTab data-id="element-7013" data-mp="3hy4wwgzu7tpu" />}
        {activeTab === 'modules' && <ModulesTab data-id="element-7014" data-mp="33ajvcrim22k1" />}
        {activeTab === 'roles' && <RolesTab data-id="element-7015" data-mp="339rxtb5gdur4" />}
        {activeTab === 'mastery' && <MasteryTab data-id="element-7016" data-mp="388lcv80e1tte" />}
        {activeTab === 'shape' && <ShapeTab data-id="element-7017" data-mp="387aa9biois3v" />}
      </motion.div>
    </PageLayout>;
}
// ─── Colors tab ──────────────────────────────────────────────────────────────
function ColorsTab() {
  const tokens = [{
    name: 'Primary',
    token: PRIMARY,
    note: 'Brand / oranje'
  }, {
    name: 'Success',
    token: SUCCESS,
    note: 'Correct / beheerst'
  }, {
    name: 'Warning',
    token: WARNING,
    note: 'Let op (niet destructief)'
  }, {
    name: 'Danger',
    token: DANGER,
    note: 'Fout / destructief'
  }, {
    name: 'Info',
    token: INFO,
    note: 'Neutraal informatief'
  }, {
    name: 'XP',
    token: XP,
    note: 'Gamification'
  }, {
    name: 'Neutral',
    token: NEUTRAL,
    note: 'Ghost / oppervlak'
  }] as const;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7018" data-mp="11chqr0d4vf3k">
      {tokens.map(({
      name,
      token,
      note
    }) => <Card key={name} className="p-4" data-id="element-7019" data-mp="1fcj0l5bdlwv6">
          <div className={`h-16 w-full rounded-xl ${token.bg} border border-black/5`} aria-hidden="true" data-id="element-7020" data-mp="1fa6b873agk83" />
        
          <div className="mt-3" data-id="element-7021" data-mp="1afitqs6pcq30">
            <div className="flex items-center justify-between gap-2" data-id="element-7022" data-mp="2qqmukbhyrl08">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-7023" data-mp="2lscze0w8okem">{name}</h4>
              <span className="text-[11px] text-gray-400" data-id="element-7024" data-mp="2lufmac23hxf4">{note}</span>
            </div>
            <dl className="mt-2 space-y-1 text-[11px]" data-id="element-7025" data-mp="35wajd79bo7ca">
              <SwatchRow label="Fill" cls={token.bg} data-id="element-7026" data-mp="3asr6pz8p6hob" />
              <SwatchRow label="Tekst" cls={token.text} data-id="element-7027" data-mp="3arg4w4ytbjzu" />
              {'border' in token && <SwatchRow label="Border" cls={token.border} data-id="element-7028" data-mp="3etkr4imxlecg" />}
            </dl>
          </div>
        </Card>)}
    </div>;
}
function SwatchRow({
  label,
  cls
}: {
  label: string;
  cls: string;
}) {
  return <div className="flex items-center justify-between gap-2" data-id="element-7029" data-mp="3okp8fp02xbag">
      <dt className="text-gray-500" data-id="element-7030" data-mp="3tj8v5rmhyr3v">{label}</dt>
      <dd data-id="element-7031" data-mp="3tm4t3fychfsb">
        <code className="font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-7032" data-mp="3tlv49vp1deo7">
          {cls}
        </code>
      </dd>
    </div>;
}
// ─── Modules tab ─────────────────────────────────────────────────────────────
function ModulesTab() {
  const entries = Object.entries(MODULE_COLOR_MAP);
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7033" data-mp="247gfjbe0prsp">
      {entries.map(([key, set]) => <Card key={key} className="overflow-hidden p-0" data-id="element-7034" data-mp="20n6bv4z2htw5">
          {/* Wereld-tint wash header met fill + hex specimen */}
          <div className="px-4 py-4 border-b border-black/5" style={{
        backgroundColor: set.tint
      }} data-id="element-7035" data-mp="1vvy40dddygl3">
          
            <div className="flex items-center gap-3" data-id="element-7036" data-mp="1qy6ajex3emes">
              <span className={`w-9 h-9 rounded-xl ${set.bg} shadow-sm border border-black/5 flex-shrink-0`} aria-hidden="true" data-id="element-7037" data-mp="1mw1hoiegxtt7" />
            
              <div className="min-w-0" data-id="element-7038" data-mp="2jkr2ohle72je">
                <code className="block font-mono text-xs text-gray-800 truncate" data-id="element-7039" data-mp="2jjo7hxh1edxo">
                  {key}
                </code>
                <span className="text-[11px] text-gray-500" data-id="element-7040" data-mp="2akuf6jaqo9px">
                  tint {set.tint}
                </span>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-1 text-[11px]" data-id="element-7041" data-mp="25olz93opwchp">
            <div className="flex items-center justify-between gap-2" data-id="element-7042" data-mp="z6ypigm7kikg">
              <span className="text-gray-500" data-id="element-7043" data-mp="u6ksruq24v1q">Hex (ring)</span>
              <span className="inline-flex items-center gap-1.5" data-id="element-7044" data-mp="u9grvmdn5prr">
                <span className="w-3 h-3 rounded-full border border-black/10" style={{
              backgroundColor: set.ring
            }} aria-hidden="true" data-id="element-7045" data-mp="1ef5nzt37ur9i" />
              
                <code className="font-mono text-gray-700" data-id="element-7046" data-mp="1n6xvbuwy1k9v">{set.ring}</code>
              </span>
            </div>
            <SwatchRow label="Gradient" cls={`${set.from} ${set.to}`} data-id="element-7047" data-mp="1if64dj9vlcj7" />
            <SwatchRow label="Tekst" cls={set.text} data-id="element-7048" data-mp="1x4ccjwqoyd38" />
            <SwatchRow label="Light" cls={set.light} data-id="element-7049" data-mp="1x310zar0811k" />
          </div>
        </Card>)}
    </div>;
}
// ─── Roles tab ───────────────────────────────────────────────────────────────
function RolesTab() {
  const ROLE_LABELS: Record<string, string> = {
    teacher: 'Leerkracht',
    parent: 'Ouder',
    learner: 'Leerling',
    admin: 'Beheerder'
  };
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7050" data-mp="bqr5ho2iv8s6">
      {Object.entries(ROLE_COLORS).map(([role, set]) => <Card key={role} className="p-4" data-id="element-7051" data-mp="7qz3ju7mw0vs">
          <div className={`rounded-xl border px-4 py-3 ${set.bg} ${set.border}`} data-id="element-7052" data-mp="7s0dtwgtsjcg">
          
            <div className="flex items-center gap-3" data-id="element-7053" data-mp="3tthm00dzkxlz">
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${set.iconBg} ${set.iconText}`} data-id="element-7054" data-mp="1nuakyln8qwh">
              
                <UserCog className="w-4 h-4" data-id="element-7055" data-mp="3k1bjvxu0szt2" />
              </span>
              <div data-id="element-7056" data-mp="3ozmsy6chr6a8">
                <p className={`text-sm font-semibold ${set.text}`} data-id="element-7057" data-mp="3oz3ljjkgw0ag">
                  {ROLE_LABELS[role] ?? role}
                </p>
                <span className={`inline-block mt-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${set.badgeBg} ${set.badgeText}`} data-id="element-7058" data-mp="3a5ql08k90fnj">
                
                  {role}
                </span>
              </div>
            </div>
          </div>
          <dl className="mt-3 space-y-1 text-[11px]" data-id="element-7059" data-mp="3622g5xn03y5z">
            <SwatchRow label="Achtergrond" cls={set.bg} data-id="element-7060" data-mp="zatadi7mueyl" />
            <SwatchRow label="Tekst" cls={set.text} data-id="element-7061" data-mp="z99o9azap9ow" />
            <SwatchRow label="Border" cls={set.border} data-id="element-7062" data-mp="149e8nc3bqoke" />
          </dl>
        </Card>)}
    </div>;
}
// ─── Mastery tab ─────────────────────────────────────────────────────────────
function MasteryTab() {
  const BAND_LABELS: Record<string, string> = {
    strong: 'Sterk beheerst',
    solid: 'Degelijk',
    developing: 'In ontwikkeling',
    attention: 'Aandacht nodig'
  };
  const thresholds = MASTERY_THRESHOLDS as Record<string, number>;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7063" data-mp="ny3xhb9r79oj">
      {Object.entries(MASTERY).map(([band, set]) => <Card key={band} className="p-4" data-id="element-7064" data-mp="1gkx58dlk9981">
          <div className="flex items-center justify-between mb-3" data-id="element-7065" data-mp="1glztedri0xk9">
            <span className="w-9 h-9 rounded-full border border-black/10" style={{
          backgroundColor: set.ring
        }} aria-hidden="true" data-id="element-7066" data-mp="1btf15go8qsiu" />
          
            <span className="text-sm font-bold text-gray-900 tabular-nums" data-id="element-7067" data-mp="16srawx7u6fpo">
              {band in thresholds ? `≥ ${thresholds[band]}` : '< 35'}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-gray-900" data-id="element-7068" data-mp="1zhy8ntzgv19v">
            {BAND_LABELS[band] ?? band}
          </h4>
          <span className="text-[11px] text-gray-400 capitalize" data-id="element-7069" data-mp="26ws2o4yqfu74">{band}</span>
          <dl className="mt-3 space-y-1 text-[11px]" data-id="element-7070" data-mp="2buaeq481qpjc">
            <SwatchRow label="Fill" cls={set.bg} data-id="element-7071" data-mp="2gsc0wy0rr4z1" />
            <div className="flex items-center justify-between gap-2" data-id="element-7072" data-mp="2grk45jvfr176">
              <dt className="text-gray-500" data-id="element-7073" data-mp="2lszm332bgrlc">Ring (hex)</dt>
              <dd data-id="element-7074" data-mp="2ptjksb2jg39l">
                <code className="font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-7075" data-mp="2pu315ncjijli">
                  {set.ring}
                </code>
              </dd>
            </div>
          </dl>
        </Card>)}
    </div>;
}
// ─── Shape tab ───────────────────────────────────────────────────────────────
function ShapeTab() {
  return <div className="space-y-5" data-id="element-7076" data-mp="34h4puaiz4ck5">
      {/* Radius */}
      <Card className="p-5" data-id="element-7077" data-mp="39hsnvg5mdtnv">
        <div className="flex items-center gap-2 mb-4" data-id="element-7078" data-mp="39ika40dbuf1h">
          <Square className="w-4 h-4 text-gray-400" data-id="element-7079" data-mp="3dj45ayecb8kz" />
          <h4 className="text-sm font-semibold text-gray-900" data-id="element-7080" data-mp="3djxhwfzzcsb1">Radius-schaal</h4>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-7081" data-mp="f1l2akhc5tig">
          {Object.entries(RADIUS).map(([name, cls]) => <div key={name} className="text-center" data-id="element-7082" data-mp="a39qw5be7ay4">
              <div className={`h-16 w-full bg-orange-100 border-2 border-orange-300 ${cls}`} aria-hidden="true" data-id="element-7083" data-mp="nv6ebw248wo6" />
            
              <p className="mt-2 text-xs font-semibold text-gray-700 capitalize" data-id="element-7084" data-mp="juml33knyd7b">
                {name}
              </p>
              <code className="text-[10px] font-mono text-gray-400" data-id="element-7085" data-mp="3sb8xtpgbuvs9">{cls}</code>
            </div>)}
        </div>
      </Card>

      {/* Shadow */}
      <Card className="p-5" data-id="element-7086" data-mp="54yhtwsx94gy">
        <div className="flex items-center gap-2 mb-4" data-id="element-7087" data-mp="558ajs5cppqc">
          <Sparkles className="w-4 h-4 text-gray-400" data-id="element-7088" data-mp="5ls94ao1jao" />
          <h4 className="text-sm font-semibold text-gray-900" data-id="element-7089" data-mp="40yhgw6e4dv">Shadow-schaal</h4>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 bg-gray-50 rounded-xl p-5" data-id="element-7090" data-mp="1gi0x8ojabnzj">
          {Object.entries(SHADOW).map(([name, cls]) => <div key={name} className="text-center" data-id="element-7091" data-mp="1k1j7nqs4208z">
              <div className={`h-16 w-full bg-white rounded-xl ${cls}`} aria-hidden="true" data-id="element-7092" data-mp="1k0q6r6fu24yr" />
            
              <p className="mt-2 text-xs font-semibold text-gray-700 capitalize" data-id="element-7093" data-mp="1o4o7tmopxpug">
                {name}
              </p>
              <code className="text-[10px] font-mono text-gray-400 break-all" data-id="element-7094" data-mp="1a5g4qipfpxkz">
                {cls}
              </code>
            </div>)}
        </div>
      </Card>

      {/* Badge variants */}
      <Card className="p-5" data-id="element-7095" data-mp="22u3wmx4ovl7i">
        <div className="flex items-center gap-2 mb-4" data-id="element-7096" data-mp="27qubxi8o6crv">
          <Boxes className="w-4 h-4 text-gray-400" data-id="element-7097" data-mp="27qat7zb73kcs" />
          <h4 className="text-sm font-semibold text-gray-900" data-id="element-7098" data-mp="1t2psud5pfz8k">
            Badge-varianten
          </h4>
        </div>
        <div className="flex flex-wrap gap-2.5" data-id="element-7099" data-mp="1y097he6bee11">
          {Object.entries(BADGE_VARIANTS).map(([name, cls]) => <span key={name} className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full border ${cls}`} data-id="element-7100" data-mp="2lq3gtlrfdkgj">
            
              {name}
            </span>)}
        </div>
      </Card>
    </div>;
}